import { useState, useCallback } from 'react'
import cl from 'clsx'
import {
    Root,
    // type DropdownMenuProps,
    Portal,
    Trigger,
    Content,
} from '@radix-ui/react-dropdown-menu'

import { useOptimisticValue } from '@/shared/hooks'

import { StatusBadge } from '../StatusBadge'
import Check from '../icons/check.svg?react'
import { STATUS_CONTENT, type StatusContent } from '../data'

import classes from './StatusSelect.module.scss'

type Value = StatusContent['value']

type StatusItemProps = {
    value: Value
    onSelect: (v: Value) => void
    selected: boolean
    allowSelect: boolean
}

const StatusItem = ({
    value,
    onSelect,
    allowSelect,
    selected,
}: StatusItemProps) => {
    const onClick = useCallback(() => {
        if (!allowSelect) return
        onSelect(value)
    }, [onSelect, value, allowSelect])

    const { checkStyle } = STATUS_CONTENT[value]

    return (
        <StatusBadge value={value} onClick={onClick} className={classes.item}>
            <div className="grow" />
            {selected && (
                <Check className={cl(classes.check)} style={checkStyle} />
            )}
        </StatusBadge>
    )
}

export type StatusSelectProps = {
    value: Value
    onChange: (v: Value) => void

    classNameTrigger?: string
    classNameContent?: string
}

export const StatusSelect = ({
    value: valueProp,
    onChange,

    classNameTrigger,
    classNameContent,
}: StatusSelectProps) => {
    const { value, setOptimisticValue } = useOptimisticValue(valueProp)
    const [open, setOpen] = useState(false)

    const onSelect: StatusItemProps['onSelect'] = useCallback(
        (value: Value) => {
            setOptimisticValue(value)
            onChange(value)
            setOpen(false)
        },
        [setOptimisticValue, onChange]
    )

    return (
        <Root open={open} onOpenChange={setOpen}>
            <Trigger className={cl(classes.trigger, classNameTrigger)}>
                <StatusBadge value={value} />
            </Trigger>
            <Portal>
                <Content
                    side="right"
                    align="start"
                    alignOffset={-11}
                    sideOffset={4}
                    className={cl(classes.content, classNameContent)}
                >
                    {Object.values(STATUS_CONTENT).map((itemProps) => {
                        return (
                            <StatusItem
                                key={itemProps.value}
                                value={itemProps.value}
                                onSelect={onSelect}
                                selected={itemProps.value === value}
                                allowSelect={itemProps.allowSelect}
                            />
                        )
                    })}
                </Content>
            </Portal>
        </Root>
    )
}
